<template>
	<section
		v-editable="blok"
		class="bg-background-secondary py-4 sm:pt-20 sm:pb-14 text-body-secondary overflow-hidden"
		:aria-label="blok.title"
	>
		<div class="container">
			<div class="flex justify-between items-center mb-8 gap-6">
				<h2 class="typo-h1 mb-4 md:mb-0 text-heading-primary">
					{{ blok.title }}
				</h2>
				<BreakpointRender :is-desktop="isDesktop">
					<template v-if="blok.children.length > 4" #desktop>
						<div class="flex shrink-0">
							<CarouselControls
								:uid="blok._uid"
								:children-length="blok.children.length"
								:swiper-index="swiperIndex"
								:swiper-slides="swiperSlides"
								:loop="swiperLoop"
								@prev="slidePrev"
								@next="slideNext"
							/>
						</div>
					</template>
				</BreakpointRender>
			</div>
			<Swiper
				class="!overflow-visible mb-4 md:mb-14"
				:modules="[Navigation, Autoplay]"
				:autoplay="{
					delay: 10000,
					disableOnInteraction: true
				}"
				:breakpoints="{1200: {spaceBetween: 16, slidesPerView: 4}, 600: {spaceBetween: 8, slidesPerView: 2}}"
				:loop="swiperLoop"
				space-between="8"
				@slide-change="handleCurrentIndex"
				@swiper="onSwiper"
			>
				<SwiperSlide
					v-for="item in blok.children"
					:key="item._uid"
				>
					<StoryblokComponent
						:blok="item"
					/>
				</SwiperSlide>
			</Swiper>
			<BreakpointRender :is-desktop="isDesktop" class="flex justify-end w-full">
				<template v-if="blok.children.length > 2" #mobile>
					<div class="inline-flex">
						<CarouselControls
							:uid="blok._uid"
							:children-length="blok.children.length"
							:swiper-index="swiperIndex"
							:swiper-slides="swiperSlides"
							:loop="swiperLoop"
							@prev="slidePrev"
							@next="slideNext"
						/>
					</div>
				</template>
			</BreakpointRender>
			<div
				v-if="blok.infoIcon.name && (!richtextIsEmpty(blok.infoText) || blok.infoTitle)"
				class="grid justify-center gap-6 px-4 py-7 bg-white shadow-sm rounded-2.5xl md:flex md:justify-start md:gap-14 md:px-10 md:py-6 mb-1"
				:class="{'mt-8': !isDesktop}"
			>
				<div class="flex justify-center items-center shrink-0 font-semibold">
					<span v-if="blok.infoIcon.icon" class="w-3.5 h-3.5 mr-2">
						<FeatherIcon :icon="blok.infoIcon.icon" class="text-icon-primary" />
					</span>
					<div v-if="blok.infoTitle">
						{{ blok.infoTitle }}
					</div>
				</div>
				<RichText :data="blok.infoText" />
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
	import {Autoplay, Navigation} from 'swiper/modules';
	import type {Swiper as SwiperClass} from 'swiper/types';
	import {Swiper, SwiperSlide} from 'swiper/vue';
	import 'swiper/css';
	import 'swiper/css/navigation';
	import {richtextIsEmpty} from '~/utils/richtextIsEmpty';
	import useTailwindBreakpoints from '~/composables/useTailwindBreakpoints';
	import type {CarouselStoryblok} from '@/storyblok/types';

	interface Props {
		blok: CarouselStoryblok
	}
	const props = defineProps<Props>();

	const breakpoints = useTailwindBreakpoints();
	const swiperIndex = ref(0);
	const swiperSlides = ref(props.blok.children.length);
	const swiperInstance = shallowRef<SwiperClass | null>(null);
	const isDesktop = breakpoints.greaterOrEqual('lg');

	const swiperLoop = computed(() => {
		return (isDesktop.value && props.blok.children.length >= 8) || (!isDesktop.value && props.blok.children.length >= 4);
	});

	function onSwiper(swiper: SwiperClass) {
		swiperInstance.value = swiper;
		handleCurrentIndex(swiper);
	}

	function handleCurrentIndex(e: SwiperClass) {
		swiperSlides.value = e.snapGrid.length;
		swiperIndex.value = e.realIndex;
	}

	function slideNext() {
		swiperInstance.value?.slideNext();
	}

	function slidePrev() {
		swiperInstance.value?.slidePrev();
	}
</script>
